import { LitElement, css, CSSResult, html, TemplateResult } from 'lit';
import {property, customElement, state} from 'lit/decorators.js';
import { AirCurtain } from './../objectModelV2/airCurtain';
import { classMap } from 'lit/directives/class-map';
import { repeat } from 'lit/directives/repeat.js';
import { store } from '../redux/store';
import { loadStyle, getTimeElapsed, dropDownClickHandler, EVENTS } from '../api/ui';
import { getConvertedTemperature } from '../api/graphDataApi';

@customElement('berner-curtain')
export class BernerCurtain extends LitElement {
    @property({ type: String })
    id: '';

    private _selected = false;
    @property()
    get selected(): string {
        return this._selected.toString();
    }

    set selected(value: string) {
        this._selected = value === 'true' ? true : false;
    }

    @state()
    _showMenu: boolean;

    @state()
    _airCurtain: AirCurtain;

    @state()
    _doorSensor = false;

    @state()
    _mode = -1;

    // class css-loading is removed as soon as the external css is loaded
    public static get styles(): CSSResult {
        return css`
            .css-loading {
                display: none;
            }
        `;
    }

    public connectedCallback(): void {
        window.addEventListener(EVENTS.CHANGE_TEMP_UNIT, this.onTempUnitChanged.bind(this) as any);
        super.connectedCallback();
    }

    public disconnectedCallback(): void {
        window.removeEventListener(EVENTS.CHANGE_TEMP_UNIT, this.onTempUnitChanged as any);
        super.disconnectedCallback();
    }

    onTempUnitChanged(e: CustomEvent): void {
        this.requestUpdate();
    }

    async firstUpdated(): Promise<void> {
        const { objectManager } = store.getState();
        // assigned id is <airCurtainID>:<siteID>
        this._airCurtain = await objectManager.getAirCurtain(
            this.id.split(':')[0]
        );

        if (!this._airCurtain.isShadowLoaded) {
            await this._airCurtain.refreshShadow();
        }

        const rowContainer = this.shadowRoot.getElementById('row-container');
        rowContainer.addEventListener('mouseover', () => {
            rowContainer.classList.add('mouse-over');
        });

        rowContainer.addEventListener('mouseout', () => {
            rowContainer.classList.remove('mouse-over');
        });

        this._doorSensor = this._airCurtain.doorOpenCount > 0;
        this._mode = this._airCurtain.mode;

        await loadStyle('./css/berner-curtain.css', this.shadowRoot);
        this.requestUpdate();
    }

    getNextEvent(): string {
        const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun'];

        if (!this._airCurtain?.schedule?.enabled) {
            return '';
        } else {
            if (this._airCurtain?.schedule.events.length == 0) return '';

            const event = this._airCurtain?.schedule.events[0];
            const hours = event.dayMinute / 60;
            const rhours = Math.floor(hours);
            const minutes = (hours - rhours) * 60;
            const rminutes = Math.round(minutes);
            const paddedMins = rminutes < 10 ? '0' + rminutes : rminutes;

            const day = days[event.weekDay];
            return `${day} ${rhours}:${paddedMins}`;
        }
    }

    onModeClicked = (e: any): void => {
        this.shadowRoot
            .querySelector('.mdc-menu-surface')
            .classList.add('show-menu');
        e.stopPropagation();
    };

    onModeItemClicked = (e: any): void => {
        this._mode = parseInt(e.currentTarget.id.split('-')[1]);

        this.shadowRoot
            .querySelector('.mdc-menu-surface')
            .classList.remove('show-menu');
        e.stopPropagation();
    };

    onDoorClicked = (e: any): void => {
        this._doorSensor = !this._doorSensor;
        e.stopPropagation();
    };

    renderMode(): TemplateResult {
        if (!this._airCurtain?.mode) return html``;

        // TODO: handle pureMode
        const modes = [
            { name: 'Off', icon: './assets/icon_mode_off.png' },
            { name: 'On', icon: './assets/icon_mode_on.png' },
            { name: 'Auto', icon: './assets/icon_mode_auto.png' },
            {
                name: 'Comfort Plus',
                icon: './assets/icon_mode_comfort_plus.png',
            },
        ];

        return html` <div class="item mode">
            <h3>Mode</h3>

            <button
                class="mdc-button mdc-button--raised mdc-button--primary mdc-tab mdc-ripple-upgraded"
                role="tab"
            >
                <span class="button-contents">
                    <img src=${modes[this._mode].icon} alt="selected mode" />
                    <span>${modes[this._mode].name}</span>
                </span>
            </button>

            <div class="mdc-menu mdc-menu-surface">
                <ul
                    class="mdc-list"
                    role="menu"
                    aria-hidden="true"
                    aria-orientation="vertical"
                    tabindex="-1"
                >
                    ${repeat(
                        modes,
                        (mode: any, index: number) => html`
                        <li
                            id="mode-${index}"
                            class="mdc-list-item"
                            role="menuitem"
                            @click=${this.onModeItemClicked}
                        >
                            <span class="mdc-list-item__ripple"></span>
                            <span class="mdc-list-item__text">
                                <img
                                    src=${mode.icon}
                                    alt="mode ${mode.name}"
                                />
                                <span>${mode.name}</span>
                            </span>
                        </li>
                        `
                    )}
                </ul>
            </div>
        </div>`;
    }

    renderDropDownButton(): TemplateResult {
        const ItemClickHandler = async (option: string, e: Event) => {
            if (option === 'curtain-remove') {
                const event = new CustomEvent(EVENTS.SHOW_REMOVE_CURTAIN, { detail: { id: this.id }});
                window.dispatchEvent(event);
            } else if (option === 'curtain-update') {
                const event = new CustomEvent(EVENTS.SHOW_UPDATE_CURTAIN, { detail: { id: this.id }});
                window.dispatchEvent(event);           
            } else if (option === 'curtain-info') {
                const event = new CustomEvent(EVENTS.SHOW_CURTAIN_INFO, { detail: { id: this.id }});
                window.dispatchEvent(event);           
            }

            e.stopPropagation();
        }

        const options = html`
        <span
            class="dropdown-item"
            id="curtain-info"
            @click=${(e: Event) => { ItemClickHandler('curtain-info', e)}}
        >
            <img src="./assets/icon_info.svg" alt="air curtain info"/>
            <span class="">Air curtain info</span>
        </span>
        <span
            class="dropdown-item"
            id="curtain-update"
            @click=${(e: Event) => { ItemClickHandler('curtain-update', e)}}
        >
            <img src="./assets/icon_gear.svg" alt="update installation"/>
            <span>Update installation</span>
        </span>
        <span
            class="dropdown-item"
            id="curtain-remove"
            @click=${(e: Event) => { ItemClickHandler('curtain-remove', e)}}
        >
            <img src="./assets/icon_trash.svg" alt="remove from site"/>
            <span>Remove from site</span>
        </span>
        `;

        return html`
        <div class="btn-group dropleft" .hidden=${!this._showMenu}>
            <button
                class="kebab"
                type="button"
                class="btn btn-light py-0"
                data-toggle="dropdown"
                aria-expanded="false"
                @click=${(e: Event) => dropDownClickHandler(e, this.shadowRoot)}
            >
                &#x22EE;
            </button>
            <div class="dropdown-menu dropdown-menu-right">${options}</div>
        </div>
        `;
    }

    renderModel(): TemplateResult {
        return html`
        <div class="col-12 col-md-3 col-lg-2 item label--model">
            <span>${this._airCurtain?.model}</span>
        </div>`;
    }

    renderElapsedTime(): TemplateResult {
        const connectivityPrescript = this._airCurtain?.connected ? 'Connected: ' : 'Online: ';
        const message = this._airCurtain?.connectivityTimestamp ? connectivityPrescript + getTimeElapsed(this._airCurtain?.connectivityTimestamp) : ''

        return html`
        <div class="col-12 col-md-4 col-lg-2">
            <div class="item label small elapsed-time">
                <span>${message}</span>
            </div>
        </div>`;
    }

    renderCalender(): TemplateResult {
        return html`
        <div class="col-3 col-lg-1 item--calender">
            <img src="./assets/icon_calendar.svg" alt="calender" />
            <span class="label--calender">${this.getNextEvent()}</span>
        </div>`;
    }

    renderTempSetPoint(): TemplateResult {
        return html`
        <div class="col-md-1 col-sm-2 item">
            <img
                src="./assets/icon_unit_heating_set_to.svg"
                alt="heating"
            />

            <span class="label">
                ${this._airCurtain?.temperatureSetPoint !== undefined
                    ? getConvertedTemperature(this._airCurtain?.temperatureSetPoint, 0) + `\u00B0`
                    : ''}
            </span>
        </div>`;
    }

    renderDoorOpenCount(): TemplateResult {
        return html`
        <div class="col-md-1 col-sm-2 item">
            <img src="./assets/icon_unit_times_door_open_active.svg" alt="doors">

            <span class="label">
                ${this._airCurtain?.doorOpenCount
                    ? this._airCurtain?.doorOpenCount
                    : ''}
            </span>
        </div>`;
    }

    renderFanSpeed(): TemplateResult {
        return html`
        <div class="col-md-1 col-sm-2 item">

            <img
                src="./assets/icon_unit_current_fan_speed_active.svg"
                alt="fan"
            />


            <span class="label">
                ${this._airCurtain?.fanSpeed
                    ? this._airCurtain?.fanSpeed
                    : ''}
            </span>
        </div>`;
    }

    renderCurrentTemperature(): TemplateResult {
        return html`
        <div class="col-md-1 col-sm-2 item">
            <img
                src="./assets/icon_unit_current_temperature.svg"
                alt="temp"
            />
            <span class="label">
                ${this._airCurtain?.sensorTemperature !== undefined
                    ? getConvertedTemperature(this._airCurtain?.sensorTemperature, 0) + `\u00B0`
                    : ''}
            </span>
        </div>`;
    }

    render(): TemplateResult {
        return html`
        <link id="css-target">
        <div class="css-loading" id="root" 
            @mouseover="${() => this._showMenu = true}"
            @mouseleave="${() => this._showMenu = false}">
            <div
                id="row-container"
                class="${classMap({
                    'row-container': true,
                    selected: this.selected === 'true',
                })}"
            >
                <section
                    class=${classMap({
                        row: true,
                        'no-gutters': true,
                        'content-row': true,
                        'col-12': true,
                    })}
                >
                    <div class="col-12 col-md-7 col-lg-2 item">
                        <span class=${classMap({'dot': this._airCurtain?.connected, 'dot--red': !this._airCurtain?.connected })}></span>
                        <span class="name"> ${this._airCurtain?.name}</span>
                    </div>
                    ${html`
                        ${this.renderElapsedTime()}
                        ${this.renderCurrentTemperature()}
                        ${this.renderDoorOpenCount()}
                        ${this.renderFanSpeed()}
                        ${this.renderTempSetPoint()}
                        ${this.renderCalender()}
                        ${this.renderModel()}
                        ${this.renderDropDownButton()}`
                    }
                </section>
            </div>
        </div>`;
    }
}
